/**
 * registry.js
 * Centralized IconRegistry for Homy bespoke icons.
 * Provides multi-variant storage, dynamic glyph registration,
 * hierarchical fallback resolution, action badge lookup, and domain metadata.
 */

import { domainMap } from "./domainMap.js";

export class IconRegistry {
  constructor() {
    this.variants = [
      "spatial-bento-duotone",
      "spatial-bento-glow",
      "spatial-bento-line",
      "obsidian-solid",
    ];

    this.registry = Object.assign(Object.create(null), {
      "spatial-bento-duotone": new Map(),
      "spatial-bento-glow": new Map(),
      "spatial-bento-line": new Map(),
      "obsidian-solid": new Map(),
    });

    this.badges = new Map();
    this.domainConfigs = new Map();
    this._isInitialized = false;
    this._initializer = null;

    // Pre-populate domain configs from canonical domainMap
    for (const [key, config] of Object.entries(domainMap)) {
      this.domainConfigs.set(key, config);
    }
  }

  /**
   * Configure a lazy initializer function to populate default glyphs/badges on demand.
   * @param {() => void} fn
   */
  setInitializer(fn) {
    this._initializer = fn;
  }

  /**
   * Ensure default glyphs/badges are registered on demand.
   */
  ensureInitialized() {
    if (!this._isInitialized && typeof this._initializer === "function") {
      this._isInitialized = true;
      this._initializer();
    }
  }

  /**
   * Register a glyph component across one or more theme variants.
   * @param {string} name - Canonical glyph key (e.g. "properties" or "plus")
   * @param {Record<string, any>} glyphDefinitions - Map of variant to component
   */
  register(name, glyphDefinitions = {}) {
    if (!name || typeof name !== "string") {
      throw new Error(`[IconRegistry] Glyph name must be a non-empty string. Got: ${name}`);
    }

    for (const [variant, Component] of Object.entries(glyphDefinitions)) {
      if (this.registry[variant] instanceof Map) {
        this.registry[variant].set(name, Component);
      } else {
        // Support custom variant extension
        this.registry[variant] = new Map();
        this.registry[variant].set(name, Component);
        if (!this.variants.includes(variant)) {
          this.variants.push(variant);
        }
      }
    }
  }

  /**
   * Register an action modifier badge component.
   * @param {string} action - Action key (e.g. "add", "edit", "filter")
   * @param {any} BadgeComponent - React component rendering badge modifier
   */
  registerBadge(action, BadgeComponent) {
    if (!action || typeof action !== "string") {
      throw new Error(`[IconRegistry] Badge action must be a non-empty string. Got: ${action}`);
    }
    this.badges.set(action, BadgeComponent);
  }

  /**
   * Register or override domain metadata.
   * @param {string} domain - Domain key
   * @param {object} config - Domain metadata config
   */
  registerDomain(domain, config) {
    this.domainConfigs.set(domain, config);
  }

  /**
   * Resolve an icon glyph component for a given name and variant,
   * applying hierarchical fallback resolution:
   * 1. Exact match in requested variant
   * 2. Fallback to "spatial-bento-duotone" (canonical default)
   * 3. Fallback to "spatial-bento-line" (minimal wireframe)
   * 4. Return null if not found
   *
   * @param {string} name - Glyph key
   * @param {string} [variant="spatial-bento-duotone"] - Requested variant
   * @returns {any|null} The resolved component or null
   */
  getGlyph(name, variant = "spatial-bento-duotone") {
    if (!name) return null;
    this.ensureInitialized();

    // 1. Exact match in requested variant
    const targetMap = this.registry[variant] instanceof Map ? this.registry[variant] : null;
    if (targetMap && targetMap.has(name)) {
      return targetMap.get(name);
    }

    // 2. Fallback to canonical default (duotone)
    if (variant !== "spatial-bento-duotone") {
      const duotoneMap = this.registry["spatial-bento-duotone"];
      if (duotoneMap && duotoneMap.has(name)) {
        return duotoneMap.get(name);
      }
    }

    // 3. Fallback to minimal wireframe (line)
    if (variant !== "spatial-bento-line") {
      const lineMap = this.registry["spatial-bento-line"];
      if (lineMap && lineMap.has(name)) {
        return lineMap.get(name);
      }
    }

    return null;
  }

  /**
   * Check if a glyph is registered for a specific variant or in any variant.
   * @param {string} name - Glyph key
   * @param {string} [variant] - Specific variant to check, or undefined for any
   * @returns {boolean}
   */
  hasGlyph(name, variant) {
    if (!name) return false;
    this.ensureInitialized();
    if (variant) {
      const targetMap = this.registry[variant] instanceof Map ? this.registry[variant] : null;
      return !!(targetMap && targetMap.has(name));
    }
    return Object.values(this.registry).some((map) => map instanceof Map && map.has(name));
  }

  /**
   * Get an action modifier badge component.
   * @param {string} action - Action key
   * @returns {any|null}
   */
  getBadge(action) {
    if (!action) return null;
    this.ensureInitialized();
    return this.badges.get(action) || null;
  }

  /**
   * Get domain metadata config.
   * @param {string} domain - Domain key
   * @returns {object|null}
   */
  getDomainConfig(domain) {
    if (!domain) return null;
    return this.domainConfigs.get(domain) || null;
  }

  /**
   * List all registered glyph names across all variants.
   * @returns {string[]}
   */
  getAllGlyphNames() {
    this.ensureInitialized();
    const names = new Set();
    for (const map of Object.values(this.registry)) {
      if (map instanceof Map) {
        for (const name of map.keys()) {
          names.add(name);
        }
      }
    }
    return Array.from(names);
  }

  /**
   * List all registered variants.
   * @returns {string[]}
   */
  getAllVariants() {
    return [...this.variants];
  }

  /**
   * Clear registry (useful for testing isolation).
   */
  clear() {
    this._isInitialized = false;
    for (const map of Object.values(this.registry)) {
      if (map instanceof Map) {
        map.clear();
      }
    }
    this.badges.clear();
    this.domainConfigs.clear();
  }
}

export const iconRegistry = new IconRegistry();
